<script lang="ts" module>
	import type { ArticleChartType, ArticleComponents, ArticleImageType } from '../../types';

	export type ArticleGeneratorProps = {
		enableContain: boolean;
		title: string;
		components: (NonNullable<ArticleComponents>[number] & {
			item: NonNullable<NonNullable<ArticleComponents>[number]>['item'];
		})[];
	};
</script>

<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { isArticleButton } from '../../utils/isArticleButton';
	import { isArticleCarousel } from '../../utils/isArticleCarousel';
	import { isArticleChart } from '../../utils/isArticleChart';
	import { isArticleImage } from '../../utils/isArticleImage';
	import { isArticleQuote } from '../../utils/isArticleQuote';
	import { isArticleText } from '../../utils/isArticleText';
	import { isArticleVideo } from '../../utils/isArticleVideo';
	import { ArticleButton } from '../ArticleButton';
	import { ArticleCarousel } from '../ArticleCarousel';
	import { ArticleChart } from '../ArticleChart';
	import { ArticleImage } from '../ArticleImage';
	import { ArticleQuote } from '../ArticleQuote';
	import { ArticleText } from '../ArticleText';
	import { ArticleVideo } from '../ArticleVideo';

	let {
		enableContain = false,
		components,
		title,
		getImageUrl,
		onClickImage = undefined,
		...rest
	}: Props = $props();

	interface Props {
		class?: string;
		enableContain?: ArticleGeneratorProps['enableContain'];
		components: ArticleGeneratorProps['components'];
		title: ArticleGeneratorProps['title'];
		getImageUrl: (path: string | null | undefined) => string;
		onClickImage?: ((imageIndex: number) => void) | undefined;
	}

	let handleClickImage = $derived((imageItem: ArticleImageType | ArticleChartType) => {
		if (!onClickImage) {
			return;
		}

		const imageIndex = components
			?.filter(({ item }) => isArticleImage(item) || isArticleChart(item))
			?.findIndex(
				({ item }) =>
					(item as ArticleImageType)?.image?.filename_disk === imageItem?.image?.filename_disk
			);

		if (imageIndex > -1) {
			onClickImage(imageIndex);
		}
	});
</script>

{#each components as { item }}
	<div class={twMerge('mb-10', rest.class)}>
		{#if isArticleImage(item)}
			<ArticleImage {getImageUrl} {enableContain} {item} onclick={handleClickImage} />
		{:else if isArticleChart(item)}
			<ArticleChart {getImageUrl} {item} onclick={handleClickImage} />
		{:else if isArticleText(item)}
			<ArticleText {item} />
		{:else if isArticleQuote(item)}
			<ArticleQuote {item} />
		{:else if isArticleButton(item)}
			<ArticleButton {item} />
		{:else if isArticleCarousel(item)}
			<ArticleCarousel {item} />
		{:else if isArticleVideo(item)}
			<ArticleVideo {item} {title} />
		{/if}
	</div>
{/each}
